import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { AlertCircleIcon, LocationIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import {
  selectApproximateStops,
  usePublishOrderStore,
  type PublishOrderLocationDraft,
} from '@hero/hooks-shared';

export interface OrderMapPreviewProps {
  className?: string;
  /** Compact mode for use inside a sidebar; defaults to full block. */
  compact?: boolean;
}

interface Stop {
  label: string;
  sub?: string;
  tone: 'origin' | 'dest';
  placeholder?: boolean;
  isApproximate?: boolean;
}

/**
 * Lightweight visual placeholder for the order route. Subscribes to the
 * Zustand publish-order store directly so it stays in sync with the form
 * without any prop drilling. Swap in the real `<OrderMap />` once we wire
 * up Google Maps for the create flow.
 */
export function OrderMapPreview({ className, compact }: OrderMapPreviewProps) {
  const { t } = useTranslation('m-order');
  const origin = usePublishOrderStore(s => s.origin);
  const destinations = usePublishOrderStore(s => s.destinations);
  const approximate = usePublishOrderStore(useShallow(selectApproximateStops));
  const approximateIds = new Set(approximate.map(stop => stopKey(stop)));

  const stops: Stop[] = [];
  if (origin) {
    stops.push({
      label: origin.city ?? origin.name,
      sub: origin.name !== origin.city ? origin.name : undefined,
      tone: 'origin',
      isApproximate: approximateIds.has(stopKey(origin)),
    });
  }
  destinations.forEach((d, idx) => {
    const hasLocation = Boolean(d.name && d.lat !== 0);
    stops.push({
      label: hasLocation
        ? d.city ?? d.name
        : t('form.destination.title', { index: idx + 1 }),
      sub: hasLocation && d.name !== d.city ? d.name : t('wizard.side.destination_empty', {
        defaultValue: 'Not chosen yet',
      }),
      tone: 'dest',
      placeholder: !hasLocation,
      isApproximate: hasLocation && approximateIds.has(stopKey(d)),
    });
  });

  const isEmpty = stops.length === 0;

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-lg border bg-gradient-to-br from-emerald-50 via-sky-50 to-indigo-50',
        compact ? 'h-[180px]' : 'h-[260px]',
        className,
      )}>
      <div
        aria-hidden
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(15,23,42,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.08) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />

      <div className="absolute top-2 start-2 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 backdrop-blur">
        {t('wizard.map.preview_badge', { defaultValue: 'Map preview' })}
      </div>

      {isEmpty ? (
        <div className="flex h-full items-center justify-center px-4 text-center text-sm text-gray-500">
          {t('wizard.map.empty', {
            defaultValue: 'Pick origin and destinations to see the route preview',
          })}
        </div>
      ) : (
        <div className="absolute inset-0 flex flex-col justify-center gap-3 p-6">
          {stops.map((s, i) => (
            <div key={`${s.label}-${i}`} className="flex items-center gap-3">
              <div className="relative flex-shrink-0">
                <div
                  className={cn(
                    'flex size-7 items-center justify-center rounded-full text-white shadow',
                    s.placeholder
                      ? 'bg-gray-300'
                      : s.tone === 'origin'
                      ? 'bg-amber-500'
                      : 'bg-sky-500',
                  )}>
                  <LocationIcon className="size-3.5" />
                </div>
                {s.isApproximate && (
                  <span
                    aria-hidden
                    className="absolute -bottom-1 -end-1 flex size-4 items-center justify-center rounded-full bg-amber-500 text-white ring-2 ring-white">
                    <AlertCircleIcon className="size-2.5" />
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className={cn(
                    'text-sm font-semibold truncate',
                    s.placeholder ? 'text-gray-500' : 'text-gray-900',
                  )}>
                  {s.label}
                </div>
                {s.sub && s.sub !== s.label && (
                  <div
                    className={cn(
                      'text-[11px] truncate',
                      s.placeholder ? 'italic text-gray-400' : 'text-gray-500',
                    )}>
                    {s.sub}
                  </div>
                )}
              </div>
              {s.isApproximate && (
                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-700 flex-shrink-0">
                  {t('wizard.location_picker.accuracy.approximate_badge', {
                    defaultValue: 'Approximate',
                  })}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function stopKey(stop: PublishOrderLocationDraft): string {
  return `${stop.lat}:${stop.lng}:${stop.name}`;
}
